import messagesInEnglish from 'app/lang/en.json';
import messagesInEnglishAU from 'app/lang/en-AU.json';
import messagesInEnglishCA from 'app/lang/en-CA.json';
import messagesInEnglishGB from 'app/lang/en-GB.json';
import messagesInEnglishIE from 'app/lang/en-IE.json';
import messagesInEnglishIN from 'app/lang/en-IN.json';
import messagesInEnglishNZ from 'app/lang/en-NZ.json';
import messagesInSpanish from 'app/lang/es-ES.json';
import messagesInFrenchCA from 'app/lang/fr-CA.json';
import messagesInFrenchFR from 'app/lang/fr-FR.json';
import messagesInGerman from 'app/lang/de-DE.json';
import messagesInHungarian from 'app/lang/hu-HU.json';
import messagesInItalian from 'app/lang/it-IT.json';
import messagesInNorwegian from 'app/lang/nb-NO.json';
import messagesInDutch from 'app/lang/nl-NL.json';
import messagesInPolish from 'app/lang/pl.json';
import messagesInPortuguese from 'app/lang/pt-PT.json';
import messagesInSlovak from 'app/lang/sk-SK.json';
import { flatten } from 'app/shared/utils/flatten';
import { useMemo } from 'react';

export const DEFAULT_LOCALE = 'en';

const messagesMap = new Map<string, Record<string, unknown>>([
  ['en', { ...messagesInEnglish.miniappContent }],
  ['en-au', { ...messagesInEnglishAU.miniappContent }],
  ['en-ca', { ...messagesInEnglishCA.miniappContent }],
  ['en-gb', { ...messagesInEnglishGB.miniappContent }],
  ['en-ie', { ...messagesInEnglishIE.miniappContent }],
  ['en-in', { ...messagesInEnglishIN.miniappContent }],
  ['en-nz', { ...messagesInEnglishNZ.miniappContent }],
  ['es', { ...messagesInSpanish.miniappContent }],
  ['es-es', { ...messagesInSpanish.miniappContent }],
  ['fr', { ...messagesInFrenchFR.miniappContent }],
  ['fr-ca', { ...messagesInFrenchCA.miniappContent }],
  ['fr-fr', { ...messagesInFrenchFR.miniappContent }],
  ['de', { ...messagesInGerman.miniappContent }],
  ['de-de', { ...messagesInGerman.miniappContent }],
  ['hu', { ...messagesInHungarian.miniappContent }],
  ['hu-hu', { ...messagesInHungarian.miniappContent }],
  ['it', { ...messagesInItalian.miniappContent }],
  ['it-it', { ...messagesInItalian.miniappContent }],
  ['nb', { ...messagesInNorwegian.miniappContent }],
  ['nb-no', { ...messagesInNorwegian.miniappContent }],
  ['nl', { ...messagesInDutch.miniappContent }],
  ['nl-nl', { ...messagesInDutch.miniappContent }],
  ['pl', { ...messagesInPolish.miniappContent }],
  ['pl-pl', { ...messagesInPolish.miniappContent }],
  ['pt', { ...messagesInPortuguese.miniappContent }],
  ['pt-pt', { ...messagesInPortuguese.miniappContent }],
  ['sk', { ...messagesInSlovak.miniappContent }],
  ['sk-sk', { ...messagesInSlovak.miniappContent }],
]);

/**
 * Normalizes a locale tag to the canonical lowercase BCP-47 form used by
 * messagesMap. Handles the POSIX underscore variant (e.g. "en_GB" → "en-gb")
 * that iOS `Locale.current.identifier` and some backend payloads produce.
 */
export const normalizeLocale = (locale: string | undefined | null): string => {
  if (!locale) {
    return DEFAULT_LOCALE;
  }
  return locale.trim().toLowerCase().replace(/_/g, '-');
};

export const getAvailableLocale = (userLocale: string): [string, string] => {
  const normalized = normalizeLocale(userLocale);

  // Exact match — use the locale's own messages and preserve the full BCP-47
  // tag so Intl.DateTimeFormat renders dates in the correct regional format.
  if (messagesMap.has(normalized)) {
    return [normalized, normalized];
  }

  // No exact match: fall back to the base language for translations, but keep
  // the original regional tag on intl.locale so date/time formatting still
  // reflects the user's region (e.g. `en-au` → messages from `en`, but dates
  // still render as "4/05/2026" rather than "5/4/2026").
  const [lang] = normalized.split('-');

  if (messagesMap.has(lang)) {
    return [lang, normalized];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData => useMemo(() => {
  const [messagesKey, locale] = getAvailableLocale(userLocale);

  return {
    locale,
    messages: flatten(messagesMap.get(messagesKey)) as Record<string, string>,
  };
}, [userLocale]);
